<template>
  <div :id="containerId" class="horizontal-scroller-container">
    <template v-for="(item, index) of resolvedItems">
      <SeriesCell
        v-if="item.type === 'SERIES'"
        :key="`${item.series.slug}-${index}`"
        :series="item.series"
        :boxset="props.showSeriesAsBoxset"
        :overlay="showOverlay"
        :show-category="props.showCategoryOnSeries"
        :textColor="props.textColor"
        :id="itemId(index)"
        :tabindex="index"
        v-focus
        @up="emit('handleUp')"
        @down="emit('handleDown')"
        @left="handleLeft(index)"
        @right="handleRight(index)"
      />
      <EpisodeCell
        v-else-if="item.type === 'VIDEO'"
        :key="`${item.video.vid}-${index}`"
        :video="item.video"
        :overlay="showOverlay"
        :id="itemId(index)"
        :tabindex="index"
        :textColor="props.textColor"
        v-focus
        @up="emit('handleUp')"
        @down="emit('handleDown')"
        @left="handleLeft(index)"
        @right="handleRight(index)"
      />
      <LiveStreamCell
        v-else-if="item.type === 'LIVE_STREAM'"
        :key="`${item.stream.currentShow?.startTime}-${index}`"
        :liveStream="item.stream"
        :id="itemId(index)"
        :tabindex="index"
        :textColor="props.textColor"
        v-focus
        @up="emit('handleUp')"
        @down="emit('handleDown')"
        @left="handleLeft(index)"
        @right="handleRight(index)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">

import { computed, onMounted, watch, getCurrentInstance } from 'vue';
import { CurrentLiveStream, SeriesResponse, SeriesVideoOrLiveStreamResponse, VideoResponseWithProgress } from '@/types/cms';
import { navigationService } from 'vue-spatialnavigation';
import { focusElementById } from '@/helpers/focus';
import { toDesignPx } from '@/helpers/scale';

import SeriesCell from '@/components/cells/SeriesCell.vue';
import EpisodeCell from '@/components/cells/EpisodeCell.vue';
import LiveStreamCell from "@/components/cells/LiveStreamCell.vue";

const SCROLL_BACK_THRESHOLD = 400;

const props = withDefaults(defineProps<{
  containerId: string;
  itemsPrefix: string;
  showOverlay?: boolean;
  showSeriesAsBoxset?: boolean;
  showCategoryOnSeries?: boolean;
  items?: SeriesVideoOrLiveStreamResponse[];
  series?: SeriesResponse[];
  videos?: VideoResponseWithProgress[];
  liveStreams?: CurrentLiveStream[];
  textColor?: string;
}>(), {
  showOverlay: false,
  showSeriesAsBoxset: false,
  showCategoryOnSeries: false,
});

const emit = defineEmits<{
  (e: 'handleUp'): void,
  (e: 'handleDown'): void,
  (e: 'handleLeftFallback'): void,
  (e: 'handleRightFallback'): void,
}>();

const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const resolvedItems = computed<SeriesVideoOrLiveStreamResponse[]>(() => {
  if (props.items) return props.items;
  if (props.series) return props.series.map((series) => ({ type: 'SERIES', series }));
  if (props.videos) return props.videos.map((video) => ({ type: 'VIDEO', video }));
  if (props.liveStreams) return props.liveStreams.map((stream) => ({ type: 'LIVE_STREAM', stream }));
  return [];
});

const itemId = (index: number) => `${props.itemsPrefix}-${index}`;

/**
 * `getBoundingClientRect` reports pixels after the design canvas's transform, so
 * on a TV that scales the canvas down the raw value would cross the threshold
 * early. `toDesignPx` puts it back in the units the constant is written in.
 */
const isScrolledBack = (element: HTMLElement) =>
  toDesignPx(element.getBoundingClientRect().left) < SCROLL_BACK_THRESHOLD;

const moveFocusSideways = (index: number, alwaysScroll: boolean) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  const container = document.getElementById(props.containerId);
  const target = document.getElementById(targetId);

  if (container && target && (alwaysScroll || isScrolledBack(target))) {
    container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  //Call helper with no scrolling - already managed above
  focusElementById(targetId);
};

const handleRight = (index: number) => {
  if (index < resolvedItems.value.length - 1) {
    moveFocusSideways(index + 1, true);
    return;
  }
  emit('handleRightFallback');
};

const handleLeft = (index: number) => {
  if (index === 0) {
    emit('handleLeftFallback');
    return;
  }
  moveFocusSideways(index - 1, false);
};


const scanForLazyImages = () => {
  setTimeout(() => {
    const container = document.getElementById(props.containerId);
    if (container) lazyLoad?.(container);
  }, 200);
};

onMounted(() => {
  scanForLazyImages();
});

//We want to watch for mutations to the items so we reload. Rails are keyed of ID so not necessarily remounted on changes
watch(() => resolvedItems.value.map((item) => {
  switch (item.type) {
    case 'SERIES':
      return item.series.slug;
    case 'VIDEO':
      return item.video.vid;
    case 'LIVE_STREAM':
      return item.stream.currentShow?.startTime //Current show start time as unique ID for current item
  }
}).join(), scanForLazyImages);

</script>

<style scoped lang="scss">
.horizontal-scroller-container {
  //Position relative for scoll calculation to not be based off of body
  position: relative;
  display: flex;
  overflow-x: auto;
  padding-top: 20px;
}

.horizontal-scroller-container > * + * {
  margin-left: $cell-spacing;
}

.horizontal-scroller-container::-webkit-scrollbar {
  display: none;
}
</style>
